import { IFRAME_HEIGHT_DEFAULT, type IframeAspectRatio, iframeUrlProblem } from "@repo/shared";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./sandboxed-iframe.i18n";

type SandboxedIframeProps = {
  url: string;
  title?: string;
  height?: number;
  aspectRatio?: IframeAspectRatio;
  className?: string;
};

const ASPECT_CLASS: Record<IframeAspectRatio, string> = {
  "16:9": "aspect-video",
  "4:3": "aspect-[4/3]",
  "1:1": "aspect-square",
};

/**
 * A ratio-sized frame (a video player) is capped and centred: its height follows its
 * width, so at full flow width (up to 1280 px) a 16:9 player fills the screen. Sized
 * with CSS aspect-ratio, not the padding-bottom trick — padding % is taken from the
 * PARENT's width, so it would ignore the cap and crop the video.
 */
export const RATIO_FRAME_MAX_WIDTH = "max-w-3xl";

/** The framed page must never be this app: `allow-same-origin` would then be a hole. */
export function isAppOrigin(url: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return true;
  }
}

/**
 * QL-776 — the only way a designer-chosen third-party page reaches the client.
 * Cross-origin by construction (https, never the app origin), so `allow-same-origin`
 * only gives the framed site ITS OWN origin (players need their storage); it can
 * never read this document or the app's storage. The API refuses platform URLs
 * on save (`isPlatformIframeUrl`: WEB_URL, the root domain and tenant hosts);
 * this render-time `isAppOrigin` check is defence in depth and also covers
 * hosts the API cannot know (previews). No allow-top-navigation: the
 * page cannot navigate the app away, and no allow-modals / allow-downloads.
 * Popups are allowed but INHERIT this sandbox (no `allow-popups-to-escape-sandbox`):
 * an escaped popup could navigate its opener's top-level window — this app — which
 * is the hole omitting allow-top-navigation closes. Inside the popup this costs
 * alert/confirm/prompt/print, downloads and pointer lock; sign-in flows still work.
 */
export function SandboxedIframe({
  url,
  title,
  height = IFRAME_HEIGHT_DEFAULT,
  aspectRatio,
  className,
}: SandboxedIframeProps) {
  const t = useT(T);
  const renderable = iframeUrlProblem(url) === null && !isAppOrigin(url.trim());
  if (!renderable) {
    return (
      <p role="alert" className="p-4 text-sm text-destructive">
        {iframeUrlProblem(url) === null ? t.appOrigin : t.invalidUrl}
      </p>
    );
  }
  const frame = (
    <iframe
      src={url.trim()}
      title={title || t.defaultTitle}
      className={aspectRatio ? "absolute inset-0 h-full w-full rounded-lg" : "w-full rounded-lg"}
      style={aspectRatio ? undefined : { height }}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture; clipboard-write"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      loading="lazy"
    />
  );
  return aspectRatio ? (
    <div
      className={cn(
        "relative mx-auto w-full",
        RATIO_FRAME_MAX_WIDTH,
        ASPECT_CLASS[aspectRatio],
        className,
      )}
    >
      {frame}
    </div>
  ) : (
    <div className={cn("w-full", className)}>{frame}</div>
  );
}
